import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store'
import { fetchDriverCashTrns, fetchOperatorCashTrns, fetchAdminCashTrns } from '../../store/slices/cashTrnReportSlice';
import { CircularProgress,Alert,  Table, TableBody, TableCell, TableHead, TableRow, Typography, Box, Paper, TableContainer } from '@mui/material';
import format from 'date-fns/format';

interface CashTransactionReportProps {
    onTripClick?: (trip: any) => void;
    onTabChange?: (tabId: string) => void;
}


const CashTransactionReport: React.FC<CashTransactionReportProps> = ({ onTripClick, onTabChange }) => {

    const dispatch = useDispatch<AppDispatch>();
    const { loading, error, driverData, operatorData, adminData } = useSelector((state: RootState) => state.cashTrnReport);
    const userDetail = useSelector((state: RootState) => state.auth.user);
    

    useEffect(() => {
        dispatch(fetchDriverCashTrns());
        if(userDetail?.role === 'ADMIN'){
            dispatch(fetchOperatorCashTrns());
            dispatch(fetchAdminCashTrns());
        }else if(userDetail?.role === 'OPERATOR'){
        dispatch(fetchOperatorCashTrns());
        }
    }, [dispatch, userDetail?.role]);

    const groupTransactionsByUser = (transactions: any[]) => {
        const groupedData: Record<string, any[]> = {};

        transactions.forEach((transaction) => {
            const userName = transaction.cashUserName || 'N/A';

            if (!groupedData[userName]) {
                groupedData[userName] = [];
            }

            groupedData[userName].push(transaction);
        });

        return groupedData;
    };

    const getFilteredOperatorData = () => {
        if (!operatorData) return [];
        
        if (userDetail?.role === 'ADMIN') {
            return operatorData;
        } else if (userDetail?.role === 'OPERATOR') {
            // Filter to show only the current operator's transactions
            return operatorData.filter(transaction => 
                transaction.cashUserName === userDetail.name || 
                transaction.cashUserName === `${userDetail.firstName} ${userDetail.lastName}`
            );
        }
        return [];
    };

    const filteredOperatorData = getFilteredOperatorData();

    const getTotalAmount = (transactions: any[]) => {
        return transactions.reduce(
            (total, transaction) => total + Number(transaction.amount),
            0
        );
    };

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 'calc(100vh - 100px)',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Error Loading Cash Transactions</Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }
    const hasNoData = driverData?.length === 0 && operatorData?.length === 0 && adminData?.length === 0;

    if (hasNoData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">
                    <Typography variant="h6">No Cash Transactions Found</Typography>
                    <Typography>There are no cash transactions to display at this time.</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto','&::-webkit-scrollbar': {
                width: '8px'
            },
            '&::-webkit-scrollbar-track': {
                background: '#1e1e1e',
                borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
                background: '#424242',
                borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
                background: '#555555'
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#424242 #1e1e1e' }}>

            {/* Driver Tables */}
            {Object.entries(groupTransactionsByUser(driverData as any)).map(([userName, items], index) => (
                <Paper key={`driver-${userName}-${index}`} sx={{ mb: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Typography variant="h6">
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 400 }}>
                                Driver:
                            </Box>
                            <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600, ml: 1 }}>
                                {userName}
                            </Box>
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'success.main', fontSize:"1rem", fontWeight: 500 }}>
                            Total: ₹{getTotalAmount(items).toFixed(2)}
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0, 85, 255, 0.1)' }}>
                                <TableCell><strong>Cash Trn Id</strong></TableCell>
                                <TableCell><strong>Trip ID</strong></TableCell>
                                <TableCell><strong>Trip Date</strong></TableCell>
                                <TableCell><strong>Amount</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow
                                    key={item.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{item.tripCode}</TableCell>
                                    <TableCell>{item.tripDate ? format(new Date(item.tripDate), 'dd MMM HH:mm') : 'N/A'}</TableCell>
                                    <TableCell>{Number(item.amount).toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                </Paper>
            ))}

            {/* Operator Tables */}
            {Object.entries(groupTransactionsByUser(filteredOperatorData as any)).map(([userName, items], index) => (
                <Paper key={`operator-${userName}-${index}`} sx={{ mb: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Typography variant="h6">
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 400 }}>
                                Operator:
                            </Box>
                            <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600, ml: 1 }}>
                                {userName}
                            </Box>
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'success.main', fontSize:"1rem", fontWeight: 500}}>
                            Total: ₹{getTotalAmount(items).toFixed(2)}
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(0, 85, 255, 0.1)' }}>
                                    <TableCell><strong>Cash Trn Id</strong></TableCell>
                                    <TableCell><strong>Trip ID</strong></TableCell>
                                    <TableCell><strong>Trip Date</strong></TableCell>
                                    <TableCell><strong>Amount</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{item.id}</TableCell>
                                        <TableCell>{item.tripCode}</TableCell>
                                        <TableCell>{item.tripDate ? format(new Date(item.tripDate), 'dd MMM HH:mm') : 'N/A'}</TableCell>
                                        <TableCell>{Number(item.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ))}

            {/* Admin Tables */}
            {userDetail?.role === 'ADMIN' && Object.entries(groupTransactionsByUser(adminData as any)).map(([userName, items], index) => (
                <Paper key={`admin-${userName}-${index}`} sx={{ mb: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Typography variant="h6">
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 400 }}>
                                Admin:
                            </Box>
                            <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600, ml: 1 }}>
                                {userName}
                            </Box>
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'success.main', fontSize:"1rem", fontWeight: 500 }}>
                            Total: ₹{getTotalAmount(items).toFixed(2)}
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0, 85, 255, 0.1)' }}>
                                <TableCell><strong>Cash Trn Id</strong></TableCell>
                                <TableCell><strong>Trip ID</strong></TableCell>
                                <TableCell><strong>Trip Date</strong></TableCell>
                                <TableCell><strong>Amount</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow
                                    key={item.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{item.id}</TableCell>
                                    <TableCell>{item.tripCode}</TableCell>
                                    <TableCell>{item.tripDate ? format(new Date(item.tripDate), 'dd MMM HH:mm') : 'N/A'}</TableCell>
                                    <TableCell>{Number(item.amount).toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                </Paper>
            ))}
        </Box>
    );
};
export default CashTransactionReport;